import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../style.css';
import { v4 as uuid } from 'uuid';
import {
  SpaceBetween,
  Button,
  Box,
  FormField,
  Input,
  Form,
  Select,
  SelectProps,
  RadioGroup,
  StatusIndicator,
  ExpandableSection,
} from '@amzn/awsui-components-react-v3';
import { Redirect } from 'react-router-dom';
import { debounce, isNaN, parseInt, isEmpty } from 'lodash';
import { useLazyQuery } from '@apollo/client';
import {
  getCategoryOptions,
  getSubcategoryOptions,
  sortOptions,
} from '../../../../utils/ContainerManagement/containerDefinitionsHelper';
import { Category } from '../../types';
import { validateInput, validateInputWithDots } from '../../../../utils';
import {
  ContainerDefinition,
  CreateContainersRequest,
  LocationDropDown,
  ParentLocation,
  RequestedContainer,
} from '../../../../services/container-management/types';
import { VALIDATE_CONTAINER_MOVEMENT } from '../../../../services/container-management/queries';
import './style.css';

export type CreateContainerProps = {
  setCreateContainersRequest: (request: CreateContainersRequest) => void;
  setHasSubmitted: (hasSubmitted: boolean) => void;
  hasSubmitted: boolean;
  categoriesMap: Map<string, Category> | null;
  containerDefinitionMap: Map<string, ContainerDefinition> | null;
  roomOptions: LocationDropDown[];
  userAlias: string;
  siteLocationId: string;
};

const CreateContainerComponent = (props: CreateContainerProps): ReactElement => {
  const {
    setCreateContainersRequest,
    setHasSubmitted,
    hasSubmitted,
    categoriesMap,
    containerDefinitionMap,
    roomOptions,
    userAlias,
    siteLocationId,
  } = props;

  const { t } = useTranslation();
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const [redirect, setRedirect] = useState('');
  const [categoryOptions, setCategoryOptions] = useState<SelectProps.Option[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<SelectProps.Option | null>(null);
  const [subcategoryOptions, setSubcategoryOptions] = useState<SelectProps.Option[]>([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState<SelectProps.Option | null>();
  const [selectedRoom, setSelectedRoom] = useState<SelectProps.Option | null>();
  const [showCategoryDropdowns, setShowCategoryDropdowns] = useState(false);
  const [locationType, setLocationType] = useState('');
  const [parentContainerName, setParentContainerName] = useState('');
  const [parentContainerNameError, setParentContainerNameError] = useState('');
  const [parentContainerValid, setParentContainerValid] = useState(false);
  const [numberOfContainers, setNumberOfContainers] = useState(1);
  const [numberOfContainersError, setNumberOfContainersError] = useState('');
  const [touchedParentContainerName, setTouchedParentContainerName] = useState(false);
  const showError = touchedParentContainerName && parentContainerName === '';
  const [debouncedInputValue, setDebouncedInputValue] = useState('');

  const handleParentContainerNameBlur = () => {
    setTouchedParentContainerName(true);
  };

  // Query validateContainerMovement
  const [validateContainerMovement, { loading: validateContainerMovementLoading }] = useLazyQuery(
    VALIDATE_CONTAINER_MOVEMENT,
    {
      fetchPolicy: 'network-only',
      onCompleted: (result) => {
        if (result?.validateContainerMovement?.movementValidated) {
          setParentContainerValid(true);
        } else {
          setParentContainerNameError('Movement to this container is not allowed');
        }
      },
      onError: (error) => {
        setParentContainerNameError(error.message);
      },
    }
  );

  const callValidateContainerMovement = useCallback(
    (request) => {
      validateContainerMovement(request);
    },
    [validateContainerMovement]
  );

  const [movableContainersDetails, setMovableContainerDetails] = useState([
    {
      id: uuid(),
      suffix: '',
      errors: {
        suffix: '',
      },
    },
  ]);

  // debounce call validate when user stops typing for 1000ms
  // https://soshace.com/how-and-when-to-debounce-and-throttle-in-react/
  const debouncedValidateContainerMovement = useMemo(() => {
    return debounce(callValidateContainerMovement, 1000);
  }, [callValidateContainerMovement]);

  useEffect(() => {
    setCategoryOptions(getCategoryOptions(categoriesMap));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoriesMap]);

  useEffect(() => {
    if (subcategoryOptions.length === 1) {
      setSelectedSubcategory(subcategoryOptions[0]);
    }

    // if there is only 1 cat/sub-cat, do not show the dropdowns
    if (subcategoryOptions.length === 1 && categoryOptions.length === 1) {
      setShowCategoryDropdowns(false);
    } else {
      setShowCategoryDropdowns(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subcategoryOptions]);

  useEffect(() => {
    setSelectedSubcategory(null);
    setSubcategoryOptions(getSubcategoryOptions(props.categoriesMap, selectedCategory));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  useEffect(() => {
    if (categoryOptions.length === 1) {
      if (selectedCategory === null) {
        setSelectedCategory(categoryOptions[0]);
      }
    } else {
      setShowCategoryDropdowns(true);
    }
  }, [categoryOptions, selectedCategory]);

  useEffect(() => {
    if (isNaN(numberOfContainers)) {
      setNumberOfContainersError('Enter number of containers');
    } else if (numberOfContainers < 1) {
      setNumberOfContainersError('Add at least 1 container');
    } else if (numberOfContainers > 50) {
      setNumberOfContainersError('Cannot add more than 50 containers');
    } else {
      setNumberOfContainersError('');
    }
  }, [numberOfContainers]);

  useEffect(() => {
    if (parentContainerNameError) {
      setParentContainerValid(false);
    }
  }, [parentContainerNameError]);

  useEffect(() => {
    setParentContainerValid(false);
    setTimeout(() => {
      setDebouncedInputValue(debouncedInputValue);
      if (selectedSubcategory && locationType === 'CONTAINER') {
        const validParentContainerNameError = validateInputWithDots(parentContainerName, true, 100);
        if (validParentContainerNameError?.trim() !== '') {
          setParentContainerNameError(validParentContainerNameError);
        } else if (!isEmpty(parentContainerName)) {
          debouncedValidateContainerMovement({
            variables: {
              sourceContainerDefinitionId: selectedSubcategory.value,
              sourceContainerName: '',
              destinationType: 'CONTAINER',
              destinationName: parentContainerName,
            },
          });
        }
      } else {
        setParentContainerNameError('');
      }
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    locationType,
    parentContainerName,
    selectedSubcategory,
    debouncedValidateContainerMovement,
    debouncedInputValue,
  ]);

  useEffect(() => {
    if (locationType === 'CUSTODY') {
      setTouchedParentContainerName(false);
    }
    if (
      hasSubmitted ||
      numberOfContainersError ||
      !selectedSubcategory ||
      !locationType ||
      (locationType === 'CONTAINER' && !parentContainerValid && showError) ||
      (locationType === 'ROOM' && !selectedRoom) ||
      (locationType === 'CONTAINER' ? parentContainerName.length === 0 : touchedParentContainerName)
    ) {
      setIsSubmitEnabled(false);
    } else {
      setIsSubmitEnabled(true);
    }
  }, [
    numberOfContainersError,
    parentContainerValid,
    selectedSubcategory,
    locationType,
    hasSubmitted,
    selectedRoom,
    touchedParentContainerName,
    parentContainerName,
    showError,
  ]);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (isSubmitEnabled && selectedSubcategory?.value && locationType) {
      let parentLocationArray: Array<ParentLocation> = [];
      if (locationType === 'CUSTODY') {
        parentLocationArray = [
          {
            type: locationType,
            value: userAlias,
          },
          {
            type: 'LAST_SITE',
            value: siteLocationId,
          },
        ];
      } else if (locationType === 'CONTAINER') {
        parentLocationArray = [
          {
            type: locationType,
            value: parentContainerName,
          },
        ];
      } else if (locationType === 'ROOM' && selectedRoom?.value) {
        parentLocationArray = [
          {
            type: locationType,
            value: selectedRoom.value,
          },
        ];
      }
      const containers: RequestedContainer[] = [];
      for (let i = 0; i < numberOfContainers; i += 1) {
        containers.push({
          // TODO remove locationId
          locationId: '',
          parentLocation: parentLocationArray,
          containerName: movableContainersDetails[i]?.suffix ?? '',
          metadata: JSON.parse('{}'),
          metadataStruct: {
            section: '',
            aisle: '',
            alphabetIdentifier: '',
            level: '',
            isCompliant: true,
          },
          containerDefinitionId: selectedSubcategory.value,
          takeCustody: locationType === 'CUSTODY',
        });
      }
      setHasSubmitted(true);
      setCreateContainersRequest({
        containers,
      });
    }
  };

  const handleReset = () => {
    setHasSubmitted(false);
    setNumberOfContainers(1);
    setMovableContainerDetails([]);
    setSelectedCategory(null);
    setParentContainerName('');
    setLocationType('');
  };

  const isContainerDefinitionMovable = () => {
    const containerDefinitionId = selectedSubcategory?.value ?? '';
    return Boolean(
      containerDefinitionMap?.get(containerDefinitionId)?.metadataConfiguration?.moveSupport
        ?.default
    );
  };

  const isContainerNamePrefixDefined = () => {
    const containerDefinitionId = selectedSubcategory?.value ?? '';
    return Boolean(
      containerDefinitionMap?.get(containerDefinitionId)?.metadataConfiguration?.containerNamePrefix
        ?.default
    );
  };

  const getContainerNamePrefix = () => {
    const containerDefinitionId = selectedSubcategory?.value ?? '';
    return String(
      containerDefinitionMap?.get(containerDefinitionId)?.metadataConfiguration?.containerNamePrefix
        ?.default
    );
  };

  // Handles number of container count change
  const handleNumberOfContainersCountChange = (count: any) => {
    const containers: any[] = [];
    if (Number(count) <= 50) {
      for (let i = 0; i < Number(count); i += 1) {
        containers.push({
          id: uuid(),
          suffix: '',
          errors: {
            suffix: '',
          },
        });
      }
      setMovableContainerDetails(containers);
      setNumberOfContainers(containers.length);
    } else {
      setMovableContainerDetails([]);
      setNumberOfContainers(Number(count));
    }
  };

  // Handles optional config input change and adds it to the movableContainerDetails
  const handleInputChange = (id: string, obj: any, name: string) => {
    const newInputFields = movableContainersDetails.map((i) => {
      if (id === i.id) {
        switch (name) {
          case 'suffix':
            // eslint-disable-next-line no-param-reassign
            i.suffix = obj.toUpperCase();
            // eslint-disable-next-line no-param-reassign
            i.errors.suffix = validateInput(i.suffix, false, true);
            break;
          default:
            break;
        }
      }
      return i;
    });
    setMovableContainerDetails(newInputFields);
  };

  const createInputField = (
    placeholder: any,
    value: any,
    onchange: any,
    id: any,
    name: string,
    disabled?: boolean
  ) => {
    if (disabled) {
      return (
        <Input
          placeholder={placeholder}
          id={id}
          className={name}
          value={value}
          name={name}
          disabled
        />
      );
    }
    return (
      <Input
        placeholder={placeholder}
        id={id}
        className={name}
        value={value}
        name={name}
        disabled={hasSubmitted}
        onChange={({ detail }) => onchange(id, detail.value, name)}
      />
    );
  };

  // Creates dropdown with parameters
  const createDropDown = (option: any, onchange: any, options: any, name: any) => {
    return (
      <Select
        data-test-id={name}
        selectedOption={option}
        onChange={({ detail }) => onchange(detail.selectedOption)}
        options={options}
        placeholder="Choose an option"
        selectedAriaLabel="Selected"
        empty="No options"
        disabled={hasSubmitted}
      />
    );
  };

  const getFormActions = () => {
    if (!hasSubmitted) {
      return (
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            data-test-id="cancelButton"
            disabled={hasSubmitted}
            onClick={() => setRedirect('/container')}
          >
            {t('Return home')}
          </Button>
          <Button
            data-test-id="submitButton"
            disabled={hasSubmitted || !isSubmitEnabled || validateContainerMovementLoading}
            variant="primary"
            onClick={handleSubmit}
          >
            {t('create-bin.title')}
          </Button>
        </SpaceBetween>
      );
    }
    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Button data-test-id="searchButton" onClick={() => setRedirect('/container')}>
          {t('Search containers')}
        </Button>
        <Button data-test-id="newContainerButton" variant="primary" onClick={() => handleReset()}>
          {t('Create another container')}
        </Button>
      </SpaceBetween>
    );
  };

  const getLocationSection = () => {
    if (selectedSubcategory?.value && isContainerDefinitionMovable()) {
      return (
        <FormField label={t('Location type')}>
          <RadioGroup
            data-test-id="locationTypeRadio"
            onChange={({ detail }) => setLocationType(detail.value)}
            value={locationType}
            items={[
              { value: 'CUSTODY', label: t('User custody'), disabled: hasSubmitted },
              { value: 'CONTAINER', label: t('Container'), disabled: hasSubmitted },
            ]}
          />
        </FormField>
      );
    }
    if (locationType !== 'ROOM') {
      setLocationType('ROOM');
    }
    // TODO add support for ROOM containers
    return (
      <FormField label={t('create-bin.room-label')}>
        <p>This container type is not supported yet.</p>
        <div className="dropDownStyle">
          {createDropDown(selectedRoom, setSelectedRoom, sortOptions(roomOptions), 'roomDropdown')}
        </div>
      </FormField>
    );
  };

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  return (
    <form onSubmit={handleSubmit}>
      <Form actions={getFormActions()}>
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xxl">
            <div className="resetButtonStyle">
              <Button
                formAction="none"
                data-test-id="resetButton"
                iconAlign="right"
                disabled={hasSubmitted}
                onClick={() => handleReset()}
              >
                {t('Reset')}
              </Button>
            </div>
          </SpaceBetween>
        </Box>
        <SpaceBetween direction="vertical" size="l">
          {showCategoryDropdowns && (
            <FormField label={t('create-bin.category-label')}>
              <div className="dropDownStyle">
                {createDropDown(
                  selectedCategory,
                  setSelectedCategory,
                  sortOptions(categoryOptions),
                  'categoryDropdown'
                )}
              </div>
            </FormField>
          )}
          {showCategoryDropdowns && (
            <FormField label={t('create-bin.subcategory-label')}>
              <div className="dropDownStyle">
                {createDropDown(
                  selectedSubcategory,
                  setSelectedSubcategory,
                  sortOptions(subcategoryOptions),
                  'subcategoryDropdown'
                )}
              </div>
            </FormField>
          )}
          {getLocationSection()}
          {locationType === 'CUSTODY' && <FormField label={t('User alias')}>{userAlias}</FormField>}
          {locationType === 'CONTAINER' && (
            <FormField label={t('Parent container name')}>
              <SpaceBetween className="vertical-align" size="s" direction="horizontal">
                <Input
                  data-test-id="parentContainerInput"
                  className="input"
                  value={parentContainerName}
                  name="parentContainerName"
                  disabled={hasSubmitted}
                  onChange={({ detail }) => setParentContainerName(detail.value)}
                  onBlur={handleParentContainerNameBlur}
                />
                {showError && (
                  <StatusIndicator type="error">Parent container name is required</StatusIndicator>
                )}
                {(() => {
                  if (parentContainerNameError ?? '') {
                    return (
                      <StatusIndicator type="error">{parentContainerNameError}</StatusIndicator>
                    );
                  }
                  if (validateContainerMovementLoading && parentContainerName.length !== 0) {
                    return (
                      <StatusIndicator data-test-id="validateLoadingStatus" type="loading">
                        {t('Validating')}
                      </StatusIndicator>
                    );
                  }
                  if (parentContainerValid && parentContainerName.length !== 0) {
                    return (
                      <StatusIndicator type="success">{t('Container name found')}</StatusIndicator>
                    );
                  }
                  return <div />;
                })()}
              </SpaceBetween>
            </FormField>
          )}
          <FormField label={t('create-bin.no-of-bins')}>
            <div
              style={{
                width: '50px',
                overflow: 'hidden',
              }}
            >
              <Input
                data-test-id="numContainersInput"
                value={isNaN(numberOfContainers) ? '' : numberOfContainers.toString()}
                name="numberOfContainers"
                disabled={hasSubmitted}
                onChange={({ detail }) => {
                  setNumberOfContainers(parseInt(detail.value));
                  handleNumberOfContainersCountChange(parseInt(detail.value));
                }}
              />
            </div>
            {numberOfContainersError && (
              <small className="binLimitError" style={{ color: 'red' }}>
                {numberOfContainersError}
              </small>
            )}
          </FormField>
          {selectedSubcategory?.value && !isContainerNamePrefixDefined() && (
            // TODO add support for container name prefix undefined containers
            <p>This container type is not supported yet.</p>
          )}

          <FormField>
            <ExpandableSection headerText="Optional Configuration" headingTagOverride="h5">
              {isContainerDefinitionMovable() && (
                <FormField label={movableContainersDetails.length > 0 ? 'Container Name' : ''}>
                  <SpaceBetween direction="horizontal" size="xs">
                    {movableContainersDetails.map((inputField) => (
                      <div
                        id="inputDiv"
                        key={inputField.id}
                        style={{
                          width: '700px',
                          overflow: 'hidden',
                        }}
                      >
                        <SpaceBetween direction="horizontal" size="xs">
                          {/* TODO: add validations for the fields  */}
                          <div className="dropDownStyle">
                            {createInputField(
                              `${getContainerNamePrefix()}.XXXX`,
                              '',
                              handleInputChange,
                              inputField.id,
                              'auto-gen',
                              true
                            )}
                          </div>
                          <div className="dropDownStyle">
                            {createInputField(
                              'User Defined Suffix',
                              inputField.suffix,
                              handleInputChange,
                              inputField.id,
                              'suffix'
                            )}
                            {inputField.errors.suffix && (
                              <small
                                className="suffixError"
                                style={{
                                  color: 'red',
                                }}
                              >
                                {inputField.errors.suffix}
                              </small>
                            )}
                          </div>
                        </SpaceBetween>
                      </div>
                    ))}
                  </SpaceBetween>
                </FormField>
              )}
            </ExpandableSection>
          </FormField>
        </SpaceBetween>
      </Form>
    </form>
  );
};

export default CreateContainerComponent;
